import { schemas } from '#oaf_schema.registry';
import { COMMERCE_AUTH_URL, STRIPE_CLIENT_ID, STRIPE_CONNECT_URL } from '~/config';
import { SubscriptionPricePlan } from '../services/pricing/models/Pricing';
import { getEncryptedStateUrl } from '../services/pricing/pricingService';
import { i18nStore } from '../stores/i18nStore';
import { pricingStore } from '../stores/pricingStore';
import { LISTING_EDITOR_ROUTE, routerStore, selectShowInputErrors } from '../stores/routerStore';
import { validateJsonSchema } from './jsonSchemaValidateUtilities';
import { logger } from './logger';
import { notifyFailure } from './notificationUtilities';
import { deepFreeze } from './objectUtilities';
import { incrementShowInputErrors, upsertQueryParams } from './routingUtilities';
const DEFAULT_PLAN = deepFreeze(schemas['pricing_plan/pricing/PricePlan'].new({
  plan_name: '',
  units: '',
  currency_code: '',
  frequency: '',
  price: undefined,
  trial_days: 0,
  active: true
}));
export const DUMMY_UUID = '00000000-0000-0000-0000-000000000000';
export const DO_NOT_SHOW_MODAL_STORAGE_KEY = 'do_not_show_sync_pricing_modal_dialog';
export const USA = 'US';
export const OTHER_COUNTRY = 'Other';
export const ZERO_DECIMAL_CURRENCIES = ['BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'];
export const CHECKOUT_SIGNUP_STATUS_LIVE = 'Live';
export const COUNTRIES_EU_MAPPING = new Map([['Austria', 'AT'], ['Belgium', 'BE'], ['Bulgaria', 'BG'], ['Croatia', 'HR'], ['Cyprus', 'CY'], ['Czech Republic', 'CZ'], ['Denmark', 'DK'], ['Estonia', 'EE'], ['Finland', 'FI'], ['France', 'FR'], ['Germany', 'DE'], ['Greece', 'GR'], ['Hungary', 'HU'], ['Ireland', 'IE'], ['Italy', 'IT'], ['Latvia', 'LV'], ['Lithuania', 'LT'], ['Luxembourg', 'LU'], ['Malta', 'MT'], ['Netherlands', 'NL'], ['Poland', 'PL'], ['Portugal', 'PT'], ['Romania', 'RO'], ['Slovakia', 'SK'], ['Slovenia', 'SI'], ['Spain', 'ES'], ['Sweden', 'SE'], ['Switzerland', 'CH'], ['United Kingdom', 'GB']]);
/**
 * I18n object is a proxy object instead of an array which we need, this function converts an i18n array-like object into a real array.
 */
export function extractI18nOptions(i18nObject) {
  const result = [];
  const keys = Object.keys(i18nObject).sort();
  for (const key of keys) {
    const obj = i18nObject[key];
    const {
      value,
      label
    } = obj;
    let description;
    if (Object.prototype.hasOwnProperty.call(obj, 'description')) {
      description = obj.description;
    }
    let disabled;
    if (Object.prototype.hasOwnProperty.call(obj, 'disabled')) {
      disabled = obj.disabled;
    }
    let classes;
    if (Object.prototype.hasOwnProperty.call(obj, 'classes')) {
      classes = obj.classes;
    }
    let inlineHelp;
    if (Object.prototype.hasOwnProperty.call(obj, 'inlineHelp')) {
      inlineHelp = {
        message: obj.inlineHelp.message,
        title: obj.inlineHelp.title
      };
      if (Object.prototype.hasOwnProperty.call(obj.inlineHelp, 'actionText')) {
        inlineHelp.actionText = obj.inlineHelp.actionText;
      }
      if (Object.prototype.hasOwnProperty.call(obj.inlineHelp, 'actionLink')) {
        inlineHelp.actionLink = obj.inlineHelp.actionLink;
      }
    }
    let tooltip;
    if (Object.prototype.hasOwnProperty.call(obj, 'tooltip')) {
      tooltip = obj.tooltip;
    }
    result.push({
      value,
      label,
      description,
      inlineHelp,
      classes,
      disabled,
      tooltip
    });
  }
  return result;
}
export function pricingModelToRecord(model, priceModelType, listingId, sourceId, org62AccountId, commerceProductId) {
  let plans;

  // We store UI STATE on the plans array to make it easier to handle re-ordering and displaying input errors, here we remove this field before sending the payload to the backend
  if (model && Array.isArray(model.plans)) {
    plans = model.plans.map(plan => ({
      ...plan,
      __UI_STATE__: undefined
    }));
  }
  return {
    source_id: sourceId,
    listing_id: listingId,
    price_model_type: priceModelType,
    external_id: '',
    // This is populated by preprocressing code on the server side
    org62AccountId: org62AccountId,
    model: {
      ...model,
      plans
    },
    commerce_external_id: commerceProductId
  };
}

// This code should mirror the validate function in the pricing-service (with the exception of different validation parameters):
// https://github.com/OneAppExchange/appx-pricing-service/blob/main/src/plans/plans.validator.ts#L12
export function validatePricingClientSide(plan, creditorIdentifier) {
  const VALIDATION_PARAMETERS = {
    skipAttributes: ['additionalProperties']
  };

  // We do not care about values that the user does not provide
  plan.source_id = plan.source_id || DUMMY_UUID;
  plan.listing_id = plan.listing_id || DUMMY_UUID;
  const errors = validateJsonSchema(plan, schemas[`pricing_plan/Pricing`].get(), VALIDATION_PARAMETERS);
  const {
    model,
    price_model_type
  } = plan;
  if (model && price_model_type) {
    let priceModelSchema;
    if (price_model_type === 'paid' && model.payment_type === 'publisher_provided') {
      priceModelSchema = schemas[`pricing_plan/pricing/pricing_model/PaidWithoutCheckout`].get();
    } else {
      priceModelSchema = getSchemaForPricingModelType(price_model_type).get();
    }
    errors.push(...validateJsonSchema(model, priceModelSchema, VALIDATION_PARAMETERS));
  }

  // For Paid with Checkout, validate that at least one plan has been sync to Stripe and is Active
  if (price_model_type === 'paid' && model.payment_type === 'salesforce_provided') {
    const validPlans = plan.model.plans.filter(el => el.stripe_id && el.active);
    if (validPlans.length < 1) {
      errors.push({
        name: 'stripe_id',
        message: i18nStore.getState().i18nStaticCache.pricing.pricingform.plans.stripeErrorActivePlan,
        stack: 'pricing_plan/pricing/pricing_model/Paid'
      });
    }
    if (model !== null && model !== void 0 && model.payment_method.includes('SEPA')) {
      if (!creditorIdentifier) {
        errors.push({
          name: 'creditor_identifier_id',
          message: i18nStore.getState().i18nStaticCache.pricing.pricingform.plans.creditorIdentifierError,
          stack: 'pricing_plan/pricing/pricing_model/Paid'
        });
      }
    }
  }
  return {
    errors,
    isValid: errors.length === 0
  };
}
export function getSchemaForPricingModelType(priceModelType) {
  let priceModelSchema;
  switch (priceModelType) {
    case 'free':
      {
        priceModelSchema = schemas['pricing_plan/pricing/pricing_model/Free'];
        break;
      }
    case 'freemium':
      {
        priceModelSchema = schemas['pricing_plan/pricing/pricing_model/Freemium'];
        break;
      }
    case 'free_to_install':
      {
        priceModelSchema = schemas['pricing_plan/pricing/pricing_model/FreeToInstall'];
        break;
      }
    case 'paid':
      {
        // For paid models there are actually 2 options, Paid and PaidWithoutCheckout
        // We don't know which is will be until the user selects a payment_type.
        // For now we can just return Paid.
        priceModelSchema = schemas['pricing_plan/pricing/pricing_model/Paid'];
        break;
      }
    default:
      {
        this.logger.error(`Price model type '${priceModelType}' is invalid and cannot be validated`);
      }
  }
  return priceModelSchema;
}

// Based on the showInputErrors url parameter will set this object to handle the required fields on the plans table
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createUIStateForPlan(plan, showInputErrors = selectShowInputErrors(routerStore.getState())) {
  return {
    requiredErrors: {
      plan_name: showInputErrors && !plan.plan_name,
      priceCurrency: showInputErrors && (!plan.price || !plan.currency_code),
      units: showInputErrors && (plan.units === 'custom' && !plan.custom_unit || !plan.units),
      frequency: showInputErrors && !plan.frequency
    }
  };
}
export function createNewDefaultPlan(index = 0) {
  return {
    ...DEFAULT_PLAN,
    index,
    __UI_STATE__: createUIStateForPlan(DEFAULT_PLAN, false)
  };
}
export function isPricingProjectionValid(data) {
  return !!(data && data.model && data.price_model_type);
}
export function handlePricingError(error) {
  if (error.response) {
    const code = error.response.status;
    logger.error(`SERVER [${code}]: ${error.response.data && error.response.data.message || undefined}`);
    let errorText = i18nStore.getState().i18nStaticCache.pricing.pricingErrors.genericerror;
    try {
      errorText = i18nStore.getState().i18nStaticCache.pricing.pricingErrors[String(error.response.data.message)];
      notifyFailure(errorText);
      return;
    } catch {
      // If the error message is not handled, we will continue and show a generic error for pricing step
      logger.warn(`Failed to save pricing: ${JSON.stringify(error.response.data)}`);
    }
  }
  notifyFailure(i18nStore.getState().i18n.notifications.listingSaveFailedSaveStep({
    STEP: i18nStore.getState().i18nStaticCache.listingStep.listingStepSetPricing,
    URL: `${LISTING_EDITOR_ROUTE}?${upsertQueryParams(['step', '2'], ['showInputErrors', incrementShowInputErrors()])}`
  }));
}
export function getLabelByValue(value, options) {
  let label = '';
  const result = options.filter(option => {
    return option.value === value;
  });
  // assume only 1 will return
  if (result.length > 0) {
    label = result[0].label;
  }
  return label;
}
/**
 * This method will invoke /encrypt endpoint in oAF and will give the Stripe connect url
 *
 * @returns Stripe connect url
 */
export async function getStripeConnectUrl() {
  const encryptedStateParam = await getEncryptedStateUrl();
  /**
   * "state" parameter is used for 1. Specify which partner should be updated in BT Commerce service and 2. Redirect back to Mercury
   */
  return `${STRIPE_CONNECT_URL}?response_type=code&client_id=${STRIPE_CLIENT_ID}&scope=read_write&redirect_uri=${COMMERCE_AUTH_URL}&state=${encryptedStateParam}`;
}
export function isPaidWithCheckout() {
  const pricingStoreState = pricingStore.getState();
  return pricingStoreState.priceModelType === 'paid' && pricingStoreState.paymentType === 'salesforce_provided';
}
export function getAlreadyHasPlans(plans) {
  if (!plans) {
    plans = pricingStore.getState().model.plans;
  }
  return plans.some(plan => plan.source_record_type === SubscriptionPricePlan.SelfCheckout && plan.active);
}
export function isNewListingWithCountryOther(isTaxCountryOther, isLegacyListing, plans) {
  return isTaxCountryOther && (!isLegacyListing || !getAlreadyHasPlans(plans));
}
export function showPricingPlans(payment_collection, publisherCountry, isLegacyListing, plans) {
  const isTaxCountryOther = publisherCountry === OTHER_COUNTRY;
  return payment_collection && !!publisherCountry && !isNewListingWithCountryOther(isTaxCountryOther, isLegacyListing, plans);
}
export function hasChangedToCheckout() {
  const pricingState = pricingStore.getState();
  return pricingState.initialPaymentType !== 'salesforce_provided' && pricingState.paymentType === 'salesforce_provided';
}